/**
 * F43-FC §6.4 — extracted from `trainer-verifications/[userId].tsx` (was the
 * inline `CertCard`). Shared by the dedicated trainer-verifications page AND the
 * platform-user Trainer panel. Props + `data-testid`s byte-identical to the
 * original. The `minutesUntil` helper + `CERT_STATUS_CHIP_MAP` are moved here so
 * the card is self-contained.
 *
 * Renders a single cert with its presigned-doc preview (5-min expiry ticker) +
 * per-cert verify / reject / revoke actions (shown only when `canAct`).
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Button,
  Card,
  CertBadge,
  DocumentTile,
  StatusChip,
  type StatusChipStatus,
} from '@bravemove/ui-web';
import type { TrainerCertificationStatus } from '@bravemove/types';
import type { AdminTrainerCertification } from '../../api/trainerVerifications';

const CERT_STATUS_CHIP_MAP: Record<TrainerCertificationStatus, StatusChipStatus> = {
  pending: 'in_review',
  verified: 'approved',
  rejected: 'rejected',
  revoked: 'rejected',
};

function minutesUntil(iso: string | null): number {
  if (!iso) return -1;
  return Math.floor((new Date(iso).getTime() - Date.now()) / 60_000);
}

export function TrainerCertCard({
  cert,
  busy,
  canAct,
  onExpired,
  onVerify,
  onReject,
  onRevoke,
}: {
  readonly cert: AdminTrainerCertification;
  readonly busy: boolean;
  readonly canAct: boolean;
  readonly onExpired: () => void;
  readonly onVerify: () => void;
  readonly onReject: () => void;
  readonly onRevoke: () => void;
}) {
  const { t } = useI18n();
  const [minsLeft, setMinsLeft] = useState(() => minutesUntil(cert.downloadUrlExpiresAt));

  useEffect(() => {
    if (!cert.downloadUrl || minsLeft <= 0) {
      if (cert.downloadUrl) onExpired();
      return;
    }
    const tick = () => {
      const mins = minutesUntil(cert.downloadUrlExpiresAt);
      setMinsLeft(mins);
      if (mins <= 0) onExpired();
    };
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cert.downloadUrlExpiresAt, cert.downloadUrl, onExpired]);

  const isExpired = !cert.downloadUrl || minsLeft <= 0;
  const isImagePreview = cert.mimeType.startsWith('image/') && !isExpired && !!cert.downloadUrl;

  return (
    <Card data-testid={`trainer-cert-${cert.id}`}>
      <div className="flex flex-col gap-3">
        {/* CertBadge renders the name + issuer header row (F33 design-polish). The
            precise 4-state lifecycle (pending/verified/rejected/revoked) is carried
            by the StatusChip beside it — CertBadge's treatment is binary
            verified/unverified, so the chip preserves the rejected/revoked states. */}
        <div className="flex items-start justify-between gap-3">
          <CertBadge
            name={cert.title}
            issuer={
              [cert.issuingBody, cert.issuedYear ? String(cert.issuedYear) : null]
                .filter(Boolean)
                .join(' · ') || '—'
            }
            verified={cert.status === 'verified'}
            className="flex-1 min-w-0"
          />
          <StatusChip
            size="sm"
            status={CERT_STATUS_CHIP_MAP[cert.status]}
            label={t(`admin:trainer-verification.cert_status.${cert.status}`)}
          />
        </div>

        <DocumentTile
          filename={cert.fileName}
          sizeBytes={cert.sizeBytes}
          status={isExpired ? 'flagged' : 'done'}
          flag={isExpired ? 'unreadable' : undefined}
          preview={isImagePreview ? (cert.downloadUrl ?? undefined) : undefined}
          onView={
            isExpired || !cert.downloadUrl
              ? undefined
              : () => window.open(cert.downloadUrl ?? '', '_blank', 'noopener')
          }
        />

        <div className="flex flex-wrap items-center gap-3 ps-0.5">
          {cert.sha256 ? (
            <span className="font-mono text-[10.5px] text-ink-3" title={cert.sha256}>
              {t('admin:trainer-verification.detail.sha256_label')}: {cert.sha256.slice(0, 12)}
              &hellip;
            </span>
          ) : null}
          {isExpired ? (
            <span className="text-[11.5px] text-warning">
              {t('admin:trainer-verification.detail.link_expired')}
            </span>
          ) : (
            <span className="text-[11.5px] text-ink-3">
              {t('admin:trainer-verification.detail.link_expiry', { minutes: minsLeft })}
            </span>
          )}
        </div>

        {cert.rejectionReason ? (
          <p className="text-[12px] text-ink-2 bg-surface-2 rounded p-2">{cert.rejectionReason}</p>
        ) : null}

        {/* Per-cert review actions — pending/verified certs can change state. */}
        {canAct ? (
          <div className="flex flex-wrap gap-2" data-testid={`trainer-cert-actions-${cert.id}`}>
            {cert.status === 'pending' ? (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  loading={busy}
                  onClick={onVerify}
                  data-testid={`cert-verify-${cert.id}`}
                >
                  {t('admin:trainer-verification.detail.cert_verify')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={busy}
                  onClick={onReject}
                  data-testid={`cert-reject-${cert.id}`}
                >
                  {t('admin:trainer-verification.detail.cert_reject')}
                </Button>
              </>
            ) : cert.status === 'verified' ? (
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={onRevoke}
                data-testid={`cert-revoke-${cert.id}`}
              >
                {t('admin:trainer-verification.detail.cert_revoke')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export default TrainerCertCard;
